import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Firmio MCP — the OAuth consent round-trip (/oauth/consent) and the
// "Aplicații conectate" list on the profile page. All calls are made as the
// signed-in user; the OAuth protocol endpoints themselves (/api/oauth/token …)
// are spoken to by the MCP client, never by this app.

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface Envelope<T> {
  success: true;
  data: T;
}

export interface ConsentInfo {
  client: {
    id: string;
    name: string | null;
    uri: string | null;
    logoUri: string | null;
  };
  /** Host of the redirect the browser will be sent back to. */
  redirectHost: string;
  scopes: string[];
}

export interface ConnectedApp {
  clientId: string;
  clientName: string | null;
  clientUri: string | null;
  logoUri: string | null;
  connectedAt: string;
  lastUsedAt: string | null;
  activeTokens: number;
}

export async function fetchConsent(req: string): Promise<ConsentInfo> {
  const res = await apiFetch<Envelope<ConsentInfo>>(
    `/api/oauth/consent?req=${encodeURIComponent(req)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function approveConsent(req: string): Promise<string> {
  const res = await apiFetch<Envelope<{ redirectTo: string }>>('/api/oauth/consent/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ req }),
  });
  return res.data.redirectTo;
}

export async function denyConsent(req: string): Promise<string> {
  const res = await apiFetch<Envelope<{ redirectTo: string }>>('/api/oauth/consent/deny', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ req }),
  });
  return res.data.redirectTo;
}

export async function listConnectedApps(): Promise<ConnectedApp[]> {
  const res = await apiFetch<Envelope<ConnectedApp[]>>('/api/oauth/connections', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function revokeConnectedApp(clientId: string): Promise<number> {
  const res = await apiFetch<Envelope<{ revoked: number }>>(
    `/api/oauth/connections/${encodeURIComponent(clientId)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data.revoked;
}
